if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      static isVariantMediaVisible(variantIds, variantId) {
        if (!variantIds || variantId == null) return false;
        if (variantIds.includes(',all,')) return true;
        return variantIds.includes(`,${variantId},`);
      }

      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mobileCarouselControls: this.querySelector('[data-mobile-carousel-controls]'),
          track: this.querySelector('.product__media-list--variant-grid'),
          pager: this.querySelector('[data-gallery-pager]'),
          count: this.querySelector('[data-gallery-count]'),
          countCurrent: this.querySelector('[data-gallery-count-current]'),
          countTotal: this.querySelector('[data-gallery-count-total]'),
          expand: this.querySelector('[data-gallery-expand]'),
          thumbs: this.querySelector('[data-gallery-thumbs]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mqlMobile = window.matchMedia('(max-width: 749px)');

        if (this.dataset.desktopLayout === 'variant_grid' && this.elements.mobileCarouselControls) {
          this.initMobileVariantCarousel();
        }

        this.elements.expand?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const slide = this.getMobileSlides()[this.getMobileActiveIndex()];
          slide?.querySelector('.product__media-toggle')?.click();
        });

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (this.dataset.desktopLayout !== 'variant_grid') return;
        queueMicrotask(() => {
          this.syncInitialVariantVisibility();
          // Also run directly: setVisibleVariant short-circuits when the
          // visible set is already correct, which skips the reset path.
          this.markDesktopGridOrphan();
        });
      }

      syncInitialVariantVisibility() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const variantSelects = productInfo.querySelector('variant-selects');
        let variant = variantSelects?.getCurrentVariant?.();

        if (!variant) {
          const variantScript = variantSelects?.querySelector('[data-selected-variant]');
          if (variantScript?.textContent) {
            try {
              variant = JSON.parse(variantScript.textContent);
            } catch (error) {
              variant = null;
            }
          }
        }

        if (!variant?.id) {
          const variantId = productInfo.querySelector('input[name="id"]')?.value;
          if (variantId) this.setVisibleVariant(variantId);
          return;
        }

        const featuredMediaKey = variant.featured_media
          ? `${productInfo.dataset.section}-${variant.featured_media.id}`
          : null;

        this.setVisibleVariant(variant.id, featuredMediaKey);
      }

      initMobileVariantCarousel() {
        this.mobilePrevBtn = this.elements.mobileCarouselControls.querySelector('[data-mobile-carousel-prev]');
        this.mobileNextBtn = this.elements.mobileCarouselControls.querySelector('[data-mobile-carousel-next]');

        this.mobilePrevBtn?.addEventListener('click', () => this.advanceMobileSlide(-1));
        this.mobileNextBtn?.addEventListener('click', () => this.advanceMobileSlide(1));

        this.mobileMqlListener = () => this.syncMobileCarousel();
        this.mqlMobile.addEventListener('change', this.mobileMqlListener);

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { sectionId } }) => {
          const productInfo = this.closest('product-info');
          if (!productInfo || String(sectionId) !== String(productInfo.dataset.section)) return;
          this.resetVariantGridCarousel();
        });

        this.initMobileDrag();
        this.syncMobileCarousel();
      }

      /*
        Mobile carousel
        ───────────────
        One canonical index: the position of the `.is-active` slide within
        getMobileSlides(). Arrows, drag-to-swipe, the track transform and the
        photo pager all read and write it through setMobileSlide() — there is
        no second carousel, media index or touch system.

        The 360° video-spin-viewer keeps exclusive ownership of horizontal
        drag on its own slide: the gesture is refused at pointerdown, so the
        track never moves under a rotate. Arrows still navigate off it.
      */
      static SWIPE_AXIS_LOCK_PX = 10;
      static SWIPE_COMMIT_RATIO = 0.18;
      static SWIPE_COMMIT_VELOCITY = 0.4; // px per ms
      static SWIPE_EDGE_RESISTANCE = 0.28;

      static isVideoSlide(slide) {
        return !!slide.querySelector('.media-type-video');
      }

      initMobileDrag() {
        if (this.mobileDragBound || !this.elements.viewer) return;
        this.mobileDragBound = true;

        this.onMobilePointerDown = this.onMobilePointerDown.bind(this);
        this.onMobilePointerMove = this.onMobilePointerMove.bind(this);
        this.onMobilePointerUp = this.onMobilePointerUp.bind(this);
        this.onMobileClickCapture = (event) => {
          if (!this.suppressNextClick) return;
          this.suppressNextClick = false;
          event.preventDefault();
          event.stopPropagation();
        };

        this.elements.viewer.addEventListener('pointerdown', this.onMobilePointerDown);
        this.elements.viewer.addEventListener('pointermove', this.onMobilePointerMove);
        this.elements.viewer.addEventListener('pointerup', this.onMobilePointerUp);
        this.elements.viewer.addEventListener('pointercancel', this.onMobilePointerUp);
        this.elements.viewer.addEventListener('click', this.onMobileClickCapture, true);

        // Drop the transition once a snap finishes so a viewport resize
        // re-anchors the track instantly instead of gliding to the new offset.
        this.onMobileTrackTransitionEnd = (event) => {
          if (event.target === this.elements.track && event.propertyName === 'transform') {
            this.elements.track.classList.remove('is-gallery-animating');
          }
        };
        this.elements.track?.addEventListener('transitionend', this.onMobileTrackTransitionEnd);
      }

      onMobilePointerDown(event) {
        this.endMobileDrag();
        this.suppressNextClick = false;
        if (!this.mqlMobile.matches || !this.elements.track) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        // The 360° viewer owns horizontal drag on its slide; arrows own taps.
        if (event.target?.closest?.('video-spin-viewer, .product-media-gallery__mobile-nav')) return;

        const total = this.getMobileSlides().length;
        if (total < 2) return;

        this.mobileDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastTime: event.timeStamp,
          velocity: 0,
          index: this.getMobileActiveIndex(),
          total,
          width: this.elements.viewer.clientWidth || 1,
          axis: null,
        };
      }

      onMobilePointerMove(event) {
        const drag = this.mobileDrag;
        if (!drag || event.pointerId !== drag.pointerId) return;

        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;

        if (!drag.axis) {
          const lock = MediaGallery.SWIPE_AXIS_LOCK_PX;
          if (Math.abs(dx) < lock && Math.abs(dy) < lock) return;

          // Vertical intent: drop the gesture so the page scrolls normally.
          if (Math.abs(dy) >= Math.abs(dx)) {
            this.endMobileDrag();
            return;
          }

          drag.axis = 'horizontal';
        }

        const elapsed = event.timeStamp - drag.lastTime;
        if (elapsed > 0) drag.velocity = (event.clientX - drag.lastX) / elapsed;
        drag.lastX = event.clientX;
        drag.lastTime = event.timeStamp;

        this.renderMobileTrack(drag.index, this.resistMobileEdge(dx, drag), false);
      }

      onMobilePointerUp(event) {
        const drag = this.mobileDrag;
        if (!drag || event.pointerId !== drag.pointerId) return;

        const dx = event.clientX - drag.startX;
        const dragged = drag.axis === 'horizontal';
        const flicked =
          Math.abs(drag.velocity) > MediaGallery.SWIPE_COMMIT_VELOCITY &&
          Math.sign(drag.velocity) === Math.sign(dx);
        const commit = dragged && (Math.abs(dx) > drag.width * MediaGallery.SWIPE_COMMIT_RATIO || flicked);

        this.endMobileDrag();
        if (!dragged) return;

        // A swipe ends with a click on whatever is under the finger, which on
        // mobile is the zoom opener covering the photo. Swallow that one click
        // so swiping never opens the modal.
        this.suppressNextClick = true;

        // Snap through the canonical setter; an uncommitted drag resolves back
        // to the slide it started on, animated rather than jumped.
        this.setMobileSlide(drag.index + (commit ? Math.sign(dx) * -1 : 0));
      }

      // Past the first/last slide the track only creeps, so the carousel feels
      // bounded without a hard stop under the finger.
      resistMobileEdge(dx, drag) {
        const atStart = drag.index === 0 && dx > 0;
        const atEnd = drag.index === drag.total - 1 && dx < 0;
        return atStart || atEnd ? dx * MediaGallery.SWIPE_EDGE_RESISTANCE : dx;
      }

      endMobileDrag() {
        this.mobileDrag = null;
      }

      getMobileSlides() {
        // Stage order matches the media list, so the featured product photo
        // is the first frame and the thumbnail row lines up with it.
        return Array.from(
          this.elements.viewer?.querySelectorAll(
            '.product__media-item--grid:not(.product__media-item--variant-hidden)'
          ) || []
        );
      }

      getMobileActiveIndex() {
        const slides = this.getMobileSlides();
        const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        return activeIndex >= 0 ? activeIndex : 0;
      }

      setMobileSlide(index, { animate = true } = {}) {
        const slides = this.getMobileSlides();
        if (!slides.length) {
          this.updateMobileNavButtons(0, 0);
          this.elements.pager?.classList.remove('is-visible');
          this.renderGalleryChrome([], 0);
          return;
        }

        const clampedIndex = Math.max(0, Math.min(index, slides.length - 1));
        slides.forEach((slide, i) => {
          slide.style.setProperty('--mobile-order', i);
          slide.classList.toggle('is-active', i === clampedIndex);
        });

        slides[clampedIndex]?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
          image.loading = 'eager';
        });

        this.updateMobileNavButtons(clampedIndex, slides.length);
        this.renderMobileTrack(clampedIndex, 0, animate);
        this.renderMobilePager(slides, clampedIndex);
        this.renderGalleryChrome(slides, clampedIndex);
      }

      advanceMobileSlide(direction) {
        if (!this.mqlMobile.matches) return;
        this.setMobileSlide(this.getMobileActiveIndex() + direction);
      }

      renderMobileTrack(index, offsetPx = 0, animate = false) {
        const { track } = this.elements;
        if (!track) return;

        if (!this.mqlMobile.matches) {
          track.classList.remove('is-gallery-animating');
          track.style.removeProperty('transform');
          return;
        }

        track.classList.toggle('is-gallery-animating', animate);
        const sign = offsetPx < 0 ? '-' : '+';
        track.style.transform = `translate3d(calc(${index * -100}% ${sign} ${Math.abs(offsetPx).toFixed(2)}px), 0, 0)`;
      }

      // The pager paginates photos only: the 360° item never gets a dot, and
      // a lone photo gets no pager at all. Counts come from the slides the
      // current variant actually exposes, so variant changes rebuild it.
      renderMobilePager(slides, activeIndex) {
        const { pager } = this.elements;
        if (!pager) return;

        const photos = slides.filter((slide) => !MediaGallery.isVideoSlide(slide));
        const activePhotoIndex = photos.indexOf(slides[activeIndex]);

        if (pager.childElementCount !== photos.length) {
          pager.replaceChildren(
            ...photos.map(() => {
              const dot = document.createElement('span');
              dot.className = 'product-media-gallery__pager-dot';
              return dot;
            })
          );
        }

        Array.from(pager.children).forEach((dot, i) => {
          dot.classList.toggle('is-current', i === activePhotoIndex);
        });

        // Hidden on the 360° slide so its hint pill is the only floating control.
        pager.classList.toggle('is-visible', photos.length > 1 && activePhotoIndex >= 0);
      }

      renderGalleryChrome(slides, activeIndex) {
        const total = slides.length;
        const { count, countCurrent, countTotal, expand, thumbs } = this.elements;
        const slide = slides[activeIndex];

        if (count && countCurrent && countTotal) {
          countCurrent.textContent = String(total ? activeIndex + 1 : 0);
          countTotal.textContent = String(total);
          const onSpin = slide && MediaGallery.isVideoSlide(slide);
          count.hidden = total < 2 || onSpin;
        }

        if (expand) {
          const canExpand = !!slide?.querySelector('.product__media-toggle');
          expand.hidden = !canExpand;
          if (canExpand) {
            expand.setAttribute('aria-label', `Open media ${activeIndex + 1} in modal`);
          }
        }

        if (!thumbs) return;

        const signature = slides.map((item) => item.dataset.mediaId).join('|');
        if (thumbs.dataset.signature !== signature) {
          thumbs.dataset.signature = signature;
          thumbs.replaceChildren(
            ...slides.map((item, index) => this.createGalleryThumb(item, index))
          );
        }

        Array.from(thumbs.children).forEach((thumb, index) => {
          if (index === activeIndex) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });

        thumbs.hidden = total < 2;

        const current = thumbs.children[activeIndex];
        if (current && thumbs.scrollWidth > thumbs.clientWidth) {
          const left = current.offsetLeft - (thumbs.clientWidth - current.offsetWidth) / 2;
          thumbs.scrollTo({ left: Math.max(0, left) });
        }
      }

      thumbSrc(url) {
        if (!url) return url;
        try {
          const next = new URL(url, window.location.origin);
          next.searchParams.set('width', '200');
          return `${next.pathname}${next.search}`;
        } catch (error) {
          return url;
        }
      }

      createGalleryThumb(slide, index) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-media-gallery__thumb';
        button.dataset.index = String(index);
        button.setAttribute('aria-label', `View photo ${index + 1}`);

        const source = slide.querySelector('img');
        const video = slide.querySelector('video');
        const src = this.thumbSrc(source?.currentSrc || source?.src || video?.getAttribute('poster'));

        if (src) {
          const image = document.createElement('img');
          image.alt = '';
          image.src = src;
          image.loading = 'lazy';
          image.decoding = 'async';
          button.appendChild(image);
        } else {
          button.classList.add('product-media-gallery__thumb--spin');
          button.textContent = '360°';
        }

        button.addEventListener('click', () => {
          this.setMobileSlide(Number(button.dataset.index));
        });

        return button;
      }

      updateMobileNavButtons(activeIndex, total) {
        if (!this.mobilePrevBtn || !this.mobileNextBtn) return;

        const singleImage = total <= 1;
        this.mobilePrevBtn.disabled = singleImage || activeIndex <= 0;
        this.mobileNextBtn.disabled = singleImage || activeIndex >= total - 1;
      }

      syncMobileCarousel() {
        this.endMobileDrag();

        if (!this.mqlMobile.matches) {
          this.renderMobileTrack(0);
          this.elements.pager?.classList.remove('is-visible');
          this.renderGalleryChrome(this.getMobileSlides(), this.getMobileActiveIndex());
          return;
        }

        this.setMobileSlide(this.getMobileActiveIndex(), { animate: false });
      }

      resetVariantGridCarousel() {
        if (this.dataset.desktopLayout !== 'variant_grid') return;

        this.endMobileDrag();
        this.setMobileSlide(this.getMobileActiveIndex(), { animate: false });
        this.markDesktopGridOrphan();
      }

      // An odd number of visible photos left the last one alone in the left
      // column of the two-column desktop grid, with a column of empty page
      // beside it. CSS can't find the last *visible* item on its own because
      // the hidden ones are still children, so the class is set here where
      // the visible set is already known.
      markDesktopGridOrphan() {
        const slides = Array.from(
          this.elements.viewer?.querySelectorAll(
            '.product__media-item--grid:not(.product__media-item--variant-hidden)'
          ) || []
        );

        slides.forEach((slide) => slide.classList.remove('product__media-item--orphan'));
        if (slides.length > 1 && slides.length % 2 === 1) {
          slides[slides.length - 1].classList.add('product__media-item--orphan');
        }
      }

      setVisibleVariant(variantId, featuredMediaId) {
        if (this.dataset.desktopLayout !== 'variant_grid') return;

        if (this.dataset.showAllMedia === 'true') {
          let firstVisible = null;

          this.elements.viewer?.querySelectorAll('[data-variant-ids]').forEach((slide) => {
            slide.classList.remove('product__media-item--variant-hidden');
            slide.classList.remove('is-active');
            if (!firstVisible) firstVisible = slide;
          });

          firstVisible?.classList.add('is-active');
          this.setModalVisibleVariant(variantId);
          this.resetVariantGridCarousel();
          return;
        }

        let firstVisible = null;
        let featuredSlide = null;
        let visibilityChanged = false;

        const slides = Array.from(this.elements.viewer?.querySelectorAll('[data-variant-ids]') || []);
        // Fallback: if the selected variant has no mapped media, show all media
        // instead of leaving the gallery empty.
        const hasMatch = slides.some((slide) =>
          MediaGallery.isVariantMediaVisible(slide.dataset.variantIds, variantId)
        );

        slides.forEach((slide) => {
          const visible = !hasMatch || MediaGallery.isVariantMediaVisible(slide.dataset.variantIds, variantId);
          if (slide.classList.contains('product__media-item--variant-hidden') !== !visible) {
            visibilityChanged = true;
          }
          slide.classList.toggle('product__media-item--variant-hidden', !visible);

          if (!visible) {
            slide.classList.remove('is-active');
            return;
          }

          slide.querySelectorAll('img[loading="lazy"]').forEach((image) => {
            image.loading = 'eager';
          });

          if (!firstVisible) firstVisible = slide;

          if (featuredMediaId && slide.dataset.mediaId === featuredMediaId) {
            featuredSlide = slide;
          }
        });

        // When the visible set is unchanged (e.g. typing in the pendant
        // builder changes the count variant but not the color), keep the
        // user's current slide instead of resetting the gallery.
        const currentActive = slides.find(
          (slide) =>
            slide.classList.contains('is-active') &&
            !slide.classList.contains('product__media-item--variant-hidden')
        );
        if (!visibilityChanged && currentActive) return;

        slides.forEach((slide) => slide.classList.remove('is-active'));
        const activeSlide = featuredSlide || firstVisible;
        activeSlide?.classList.add('is-active');

        this.setModalVisibleVariant(variantId);
        this.resetVariantGridCarousel();
      }

      setModalVisibleVariant(variantId) {
        const productInfo = this.closest('product-info');
        const modal = document.querySelector(`#ProductModal-${productInfo?.dataset.section}`);
        if (!modal) return;

        const items = Array.from(modal.querySelectorAll('[data-variant-ids]'));
        // Mirror the gallery fallback so the modal always matches what is visible.
        const hasMatch = items.some((item) =>
          MediaGallery.isVariantMediaVisible(item.dataset.variantIds, variantId)
        );

        items.forEach((item) => {
          const visible = !hasMatch || MediaGallery.isVariantMediaVisible(item.dataset.variantIds, variantId);
          item.classList.toggle('product__media-item--variant-hidden', !visible);
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        if (this.mobileMqlListener) {
          this.mqlMobile.removeEventListener('change', this.mobileMqlListener);
        }
        if (this.mobileDragBound && this.elements.viewer) {
          this.elements.viewer.removeEventListener('pointerdown', this.onMobilePointerDown);
          this.elements.viewer.removeEventListener('pointermove', this.onMobilePointerMove);
          this.elements.viewer.removeEventListener('pointerup', this.onMobilePointerUp);
          this.elements.viewer.removeEventListener('pointercancel', this.onMobilePointerUp);
          this.elements.viewer.removeEventListener('click', this.onMobileClickCapture, true);
          this.elements.track?.removeEventListener('transitionend', this.onMobileTrackTransitionEnd);
          this.mobileDragBound = false;
        }
        this.endMobileDrag();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
